import axios from 'axios';
import { CURR_API } from '@/constants/apis';
import { refusalMessage } from '@/api/refusals';
import {
  NOTE_NOT_NOTIFIED_EITHER_TEXT,
  NOTE_NOT_NOTIFIED_TEXT,
  NOTE_VIDEO_NOT_ATTACHED_TEXT,
  noteVideoUnboundText,
  normalizeNoteAttachments,
} from '@/utils/noteAttachments';

/**
 * Saving a patient note (keyon tswebhook.php, API_Meth SavePatientNote).
 *
 * The save is JSON, as it has always been. A note that carries video names the
 * finished uploads in attachment_uids (and the ones the patient may see in
 * patient_visible_uids); a note without video posts exactly the body it always
 * did. The note text endpoints are otherwise untouched.
 *
 * saveNote never throws: every way a save can end is an `outcome`.
 *   saved               the note is saved and every video it named is on it (or none was named)
 *   saved_partial       saved; keyon could not attach some videos and has released them
 *   saved_without_video saved, but there is no sign keyon read attachment_uids (an older
 *                       replica ignores the field and answers Inserted)
 *   refused             keyon refused the save with a status 4xx or 5xx; nothing was saved
 *   failed              no answer, or an answer that is neither Inserted nor carries a note
 *                       id; nothing known to be saved
 *
 * A saved outcome whose notification did not go out (msg is not Inserted, but a
 * note id is there) says so in `message`; it is still saved, never failed, so the
 * page does not offer to save the same note again.
 */

export const NOTE_API_URL = `${CURR_API}/tswebhook.php`;

const isPositiveInteger = (value) => Number.isSafeInteger(value) && value > 0;
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function result(outcome, extra = {}) {
  return {
    outcome,
    ok: outcome === 'saved' || outcome === 'saved_partial',
    noteSaved: outcome === 'saved' || outcome === 'saved_partial' || outcome === 'saved_without_video',
    noteId: null,
    unbound: [],
    attachments: [],
    message: null,
    code: null,
    ...extra,
  };
}

// A refusal as the page words it: keyon's own sentence for a code it owns, else
// none, so the caller's generic alert stands.
function refused(status, data) {
  const body = isObject(data) ? data : {};
  return result('refused', {
    message: refusalMessage(body),
    code: typeof body.code === 'string' ? body.code : null,
    status,
  });
}

// What a 200 says about a save that named videos. A keyon that read the request
// answers note_id and attachments_unbound (a list, empty when all were attached);
// without either, it is a keyon from before notes took video and the note is on
// the server with no video.
function videoOutcome(data, uids) {
  const answered = isPositiveInteger(data.note_id) && Array.isArray(data.attachments_unbound);
  if (!answered) {
    return result('saved_without_video', { unbound: uids.slice(), message: NOTE_VIDEO_NOT_ATTACHED_TEXT });
  }
  const attachments = normalizeNoteAttachments({ attachments: data.attachments });
  const attached = new Set(attachments.map((item) => item.file_uid).filter(Boolean));
  const told = new Set(data.attachments_unbound.filter((uid) => typeof uid === 'string'));
  // A video the answer neither attaches nor reports is not on the note either.
  const unbound = uids.filter((uid) => told.has(uid) || !attached.has(uid));
  const base = { noteId: data.note_id, attachments };
  if (unbound.length) {
    return result('saved_partial', { ...base, unbound, message: noteVideoUnboundText(unbound.length) });
  }
  return result('saved', base);
}

// A saved note whose notification did not go out: the same outcome, told.
function notNotified(saved) {
  if (!saved.noteSaved) return saved;
  const message = saved.message ? `${saved.message} ${NOTE_NOT_NOTIFIED_EITHER_TEXT}` : NOTE_NOT_NOTIFIED_TEXT;
  return { ...saved, message };
}

/**
 * @param {object} post the note, as NotePopup builds it (API_Meth, PatientNote, patientID, ...)
 * @param {object} [options]
 * @param {string[]} [options.attachmentUids] finished video uploads to put on the note
 * @param {string[]} [options.visibleUids] those of them the patient may play (the rest are staff only)
 * @param {{post: Function}} [options.client] axios, or anything with its post
 */
export async function saveNote(post, { attachmentUids, visibleUids, client = axios } = {}) {
  const uids = Array.from(new Set(Array.isArray(attachmentUids) ? attachmentUids : []));
  let body = post;
  if (uids.length) {
    // keyon matches the patient exactly against the id the upload named, which
    // went up as text; a number here would be refused as no patient at all.
    body = { ...post, patientID: String(post.patientID), attachment_uids: uids };
    const visible = (Array.isArray(visibleUids) ? visibleUids : []).filter((uid) => uids.includes(uid));
    // keyon refuses a visible video that is not attached, so none is named that is not.
    if (visible.length) body.patient_visible_uids = Array.from(new Set(visible));
  }

  let res;
  try {
    res = await client.post(NOTE_API_URL, body, { headers: { 'Content-Type': 'application/json;' } });
  } catch (error) {
    const response = error && error.response;
    if (response && typeof response.status === 'number') return refused(response.status, response.data);
    return result('failed');
  }

  const status = res && typeof res.status === 'number' ? res.status : 200;
  if (status >= 400) return refused(status, res.data);
  const data = res ? res.data : null;
  if (!isObject(data)) return result('failed');
  // keyon answers msg 'Inserted' when the note went in and its notification did
  // (or none was asked for). When the notification step did not report success,
  // msg is InsertPatientNotes' object instead; a note it did insert still comes
  // with its note_id (and, for a save with video, what became of the videos).
  // Without a note id, anything but Inserted is what it always was: not saved.
  const notified = data.msg === 'Inserted';
  if (!notified && !isPositiveInteger(data.note_id)) return result('failed');
  const saved = uids.length
    ? videoOutcome(data, uids)
    : result('saved', { noteId: isPositiveInteger(data.note_id) ? data.note_id : null });
  return notified ? saved : notNotified(saved);
}
